import { useState, useEffect } from "react";
import { marked } from "marked";
import { useAppContext } from "../App";

interface Story {
  id: number;
  title: string;
  body: string;
  user_id: number;
  username: string;
  profile_pic_url: string | null;
  like_count: number;
  word_count: number;
  created_at: string;
  hasLiked: boolean;
}

interface StoryPageProps {
  storyId: number;
  onBack: () => void;
}

export function StoryPage({ storyId, onBack }: StoryPageProps) {
  const { user, likes, setLikes, isAdmin, refreshData } = useAppContext();
  const [story, setStory] = useState<Story | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [likeCount, setLikeCount] = useState(0);
  const [fontSize, setFontSize] = useState(() => {
    const stored = localStorage.getItem("borges-font-size");
    return stored ? parseFloat(stored) : 1;
  });
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [editBody, setEditBody] = useState("");
  const [saving, setSaving] = useState(false);

  const hasLiked = likes.includes(storyId);
  const isOwner = user && story && user.id === story.user_id;
  const canDelete = isOwner || isAdmin;

  useEffect(() => {
    const fetchStory = async () => {
      try {
        const res = await fetch(`/api/story/${storyId}`);
        if (!res.ok) {
          throw new Error("Story not found");
        }
        const data = await res.json();
        setStory(data);
        setLikeCount(data.like_count);
        setEditTitle(data.title);
        setEditBody(data.body);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load story");
      } finally {
        setLoading(false);
      }
    };
    fetchStory();
  }, [storyId]);

  const handleLike = async () => {
    if (!user) {
      window.location.href = "/auth/login";
      return;
    }

    try {
      const res = await fetch(`/api/like/${storyId}`, { method: "POST" });
      const data = await res.json();
      setLikeCount(data.like_count);
      if (data.liked) {
        setLikes([...likes, storyId]);
      } else {
        setLikes(likes.filter((id) => id !== storyId));
      }
    } catch (err) {
      console.error("Failed to toggle like:", err);
    }
  };

  const handleSave = async () => {
    if (!editTitle.trim() || !editBody.trim()) return;
    setSaving(true);
    try {
      const res = await fetch(`/api/story/${storyId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title: editTitle, body: editBody }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || "Failed to save");
      }
      const updated = await res.json();
      setStory({ ...story!, ...updated });
      setIsEditing(false);
      refreshData();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm("Are you sure you want to delete this story?")) return;
    try {
      const res = await fetch(`/api/story/${storyId}`, { method: "DELETE" });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || "Failed to delete");
      }
      refreshData();
      onBack();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete");
    }
  };

  const changeFontSize = (delta: number) => {
    const newSize = Math.max(0.8, Math.min(1.4, fontSize + delta));
    setFontSize(newSize);
    localStorage.setItem("borges-font-size", newSize.toString());
  };

  if (loading) {
    return <div className="story-page loading">Loading...</div>;
  }

  if (error || !story) {
    return (
      <div className="story-page error">
        <button className="back-btn" onClick={onBack}>
          &larr; Back
        </button>
        <p>{error || "Story not found"}</p>
      </div>
    );
  }

  const htmlContent = marked(story.body, { breaks: true }) as string;
  const wordCount = editBody.trim().split(/\s+/).filter(w => w.length > 0).length;

  return (
    <div className="story-page">
      <button className="back-btn" onClick={onBack}>
        &larr; Back
      </button>

      <article className="story-article">
        <header className="story-header">
          {isEditing ? (
            <input
              type="text"
              className="edit-title-input"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              placeholder="Title"
            />
          ) : (
            <h1 className="story-title">{story.title}</h1>
          )}
          <div className="story-meta">
            <span className="story-author">@{story.username}</span>
            <button
              className={`like-btn ${hasLiked ? "liked" : ""}`}
              onClick={handleLike}
            >
              <span className="heart">{hasLiked ? "♥" : "♡"}</span>
              <span>{likeCount}</span>
            </button>
          </div>
        </header>

        <div className="reading-controls">
          <button
            className="font-btn"
            onClick={() => changeFontSize(-0.1)}
            title="Decrease font size"
          >
            A-
          </button>
          <button
            className="font-btn"
            onClick={() => changeFontSize(0.1)}
            title="Increase font size"
          >
            A+
          </button>
          {isOwner && !isEditing && (
            <button className="edit-btn" onClick={() => setIsEditing(true)}>
              Edit
            </button>
          )}
          {canDelete && !isEditing && (
            <button className="delete-btn" onClick={handleDelete}>
              Delete
            </button>
          )}
        </div>

        {isEditing ? (
          <div className="edit-form">
            <textarea
              className="edit-body-input"
              value={editBody}
              onChange={(e) => setEditBody(e.target.value)}
              placeholder="Story body (Markdown supported)"
              rows={20}
            />
            <div className="edit-footer">
              <span className={`word-count ${wordCount > 3000 ? "over-limit" : ""}`}>
                {wordCount} / 3000 words
              </span>
              <div className="edit-actions">
                <button
                  className="cancel-btn"
                  onClick={() => {
                    setIsEditing(false);
                    setEditTitle(story.title);
                    setEditBody(story.body);
                  }}
                >
                  Cancel
                </button>
                <button
                  className="save-btn"
                  onClick={handleSave}
                  disabled={saving || wordCount > 3000}
                >
                  {saving ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div
            className="story-body"
            style={{ fontSize: `${fontSize}rem` }}
            dangerouslySetInnerHTML={{ __html: htmlContent }}
          />
        )}
      </article>
    </div>
  );
}
